import { FC, useCallback, useMemo } from "react"
import { useField, ErrorMessageProps, useFormikContext } from "formik"
import {
  AlertIcon,
  SelectField as MarmaladeSelectField,
  SelectFieldProps as MarmaladeSelectFieldProps,
  SuccessIcon
} from "@eventbrite/marmalade"
import {
  DropdownMenuOptions,
  HandleCountryOfViolationSelection,
  HandleTopicSelection
} from "../../ContactForm/ContactForm"

// styles
import * as styles from "./SelectField.module.scss"

export type HandleChange = (
  value: string,
  name: string,
  event: React.ChangeEvent<HTMLSelectElement>
) => void

type ValidationAdornment = {
  success: JSX.Element
  error: JSX.Element
  none: null
}

export interface Props extends MarmaladeSelectFieldProps {
  name: string
  errorMessageProps?: ErrorMessageProps
  subtopicOptions?: DropdownMenuOptions[]
  euCountries?: readonly string[]
  handleTopicSelection?: HandleTopicSelection
  handleCountrySelection?: HandleCountryOfViolationSelection
  required?: boolean
}

export const SelectField: FC<Props> = ({
  name,
  label,
  helperText,
  subtopicOptions,
  euCountries,
  handleTopicSelection = null,
  handleCountrySelection = null,
  required = false,
  ...otherProps
}) => {
  const [field, meta, helpers] = useField(name)
  const { submitCount } = useFormikContext()

  const validationAdornment = useMemo<ValidationAdornment>(
    () => ({
      success: <SuccessIcon size="small" />,
      error: <AlertIcon size="small" />,
      none: null
    }),
    []
  )

  const handleChange: HandleChange = useCallback(
    (value) => {
      helpers.setValue(value, true)
      helpers.setTouched(true, true)
    },
    [helpers]
  )

  const handleBlur = useCallback(() => {
    helpers.setTouched(true, true)
  }, [helpers])

  const showError = meta.touched || submitCount > 0

  return (
    <div
      className={styles?.selectFieldWrapper}
      style={{ marginBottom: "1.5rem" }}
    >
      <MarmaladeSelectField
        {...field}
        {...otherProps}
        label={label}
        name={name}
        value={meta.value || ""}
        onChange={(value, name, event) => {
          if (subtopicOptions) handleTopicSelection?.(value, subtopicOptions)
          if (euCountries) handleCountrySelection?.(value, euCountries)
          handleChange(value, name, event)
        }}
        onBlur={handleBlur}
        hasError={showError && !!meta.error}
        validationAdornment={
          showError
            ? meta.error
              ? validationAdornment.error
              : validationAdornment.success
            : validationAdornment.none
        }
        helperText={showError && meta.error ? meta.error : helperText}
        required={required}
      />
    </div>
  )
}
